import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams } from "react-router-dom";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  createSaleOrder,
  updateSaleOrder,
  getSingleSaleOrder,
  searchParties,
  searchProduct,
} from "../../services/services";
import { ArrowLeft, ChevronDown } from "lucide-react";
import AsyncSearchSelect from "./components/AsyncSearchSelect";

const SaleOrderPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [orderData, setOrderData] = useState({
    vendor: "",
    vendorName: "",
    saleDate: new Date().toISOString().split("T")[0],
    items: [{ productId: "", quantity: 1, salePrice: 0 }],
    notes: "",
    receivedAmount: 0,
    paymentMode: "cash",
  });
  const [cachedOptions, setCachedOptions] = useState([]);
  const [cachedProductOptions, setCachedProductOptions] = useState([]);

  const loadProductOptions = async (input) => {
    if (!input || input.trim().length < 2) {
      return cachedProductOptions;
    }

    try {
      const res = await searchProduct({ search: input });

      const products = res?.data?.response || [];

      const options = products.map((p) => ({
        label: p.name,
        value: p._id,
        raw: p,
      }));

      // cache
      setCachedProductOptions(options);

      return options;
    } catch (err) {
      console.error("Product search failed:", err);
      return cachedProductOptions;
    }
  };

  const loadPartyOptions = async (input) => {
    // If input too short → return cached
    if (!input || input.trim().length < 2) {
      return cachedOptions;
    }

    try {
      const res = await searchParties({ search: input });

      let parties = res?.data?.response || [];

      // Only customer & both
      parties = parties.filter(
        (p) => p.partyType === "customer" || p.partyType === "both",
      );

      const options = parties.map((p) => ({
        label: `${p.name}${p.mobile ? ` (${p.mobile})` : ""}`,
        value: p._id,
        raw: p,
      }));

      // cache
      setCachedOptions(options);

      return options;
    } catch (err) {
      console.error("Party search failed:", err);
      return cachedOptions;
    }
  };

  const clearSearchCache = () => {};

  useEffect(() => {
    const resizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        const overlay = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (overlay) {
          overlay.style.display = "none";
        }
      }
    };

    window.addEventListener("error", resizeObserverError);
    return () => window.removeEventListener("error", resizeObserverError);
  }, []);

  useEffect(() => {
    if (!id) return;

    const fetchSaleOrder = async () => {
      setLoading(true);
      try {
        const res = await getSingleSaleOrder(id);
        const order = res.data.response;

        setOrderData({
          vendor: order.customerId?._id || "",
          vendorName: order.customerId?.name || "",
          saleDate: order.saleDate
            ? order.saleDate.split("T")[0]
            : new Date().toISOString().split("T")[0],

          items: (order.items || []).map((item) => ({
            productId: item.productId?._id || item.productId,
            itemName: item.productId?.name || item.itemName,
            quantity: item.sellingQty || 1,
            salePrice: item.sellingPrice || 0,
          })),

          notes: order.notes || "",
          receivedAmount: order.receivedAmount || 0,
          paymentMode: order.paymentMode || "cash",
        });
      } catch (err) {
        toast.error("Failed to load sale order");
      } finally {
        setLoading(false);
      }
    };

    fetchSaleOrder();
  }, [id]);

  const handleItemChange = (index, field, value) => {
    setOrderData((prev) => {
      const items = [...prev.items];

      let updatedValue = value;

      // ---- Quantity (numbers only) ----
      if (field === "quantity") {
        updatedValue = value.replace(/[^0-9]/g, "");
      }

      // ---- Sale Price (allow decimal) ----
      if (field === "salePrice") {
        if (!/^\d*\.?\d*$/.test(value)) return prev;
      }

      items[index] = {
        ...items[index],
        [field]: updatedValue,
      };

      return { ...prev, items };
    });
  };

  const addItem = () => {
    setOrderData((prev) => ({
      ...prev,
      items: [...prev.items, { productId: "", quantity: 1, salePrice: 0 }],
    }));
  };

  const removeItem = (index) => {
    if (orderData.items.length === 1) {
      toast.warn("At least one item required");
      return;
    }
    setOrderData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const grandTotal = orderData.items.reduce(
    (sum, item) => sum + item.quantity * item.salePrice,
    0,
  );
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        // fontWeight: "600",
        // fontSize: "14px",
        textTransform: "uppercase",
      },
    },
  };
  // DataTable columns
  const columns = useMemo(
    () => [
      {
        name: "Product",
        grow: 2,
        cell: (row, index) => (
          <div className="py-2 w-100">
            {/* <ItemAutoSuggest
              selectedItemId={row.productId}
              selectedItemName={
                items.find((i) => i._id === row.productId)?.name || ""
              }
              onSelect={(item) => {
                setOrderData((prev) => {
                  const updated = [...prev.items];

                  updated[index] = {
                    ...updated[index],
                    productId: item._id,
                    salePrice: item.salePrice || 0,
                  };

                  return { ...prev, items: updated };
                });
              }}
            /> */}{" "}
            <AsyncSearchSelect
              value={
                row.productId
                  ? {
                      label: row.itemName || "Selected Item",
                      value: row.productId,
                    }
                  : null
              }
              loadOptions={loadProductOptions}
              onChange={(selected) => {
                const product = selected?.raw;

                setOrderData((prev) => {
                  const updated = [...prev.items];

                  updated[index] = {
                    ...updated[index],
                    productId: product?._id || "",
                    itemName: product?.name || "",
                    salePrice: product?.salePrice || 0,
                    quantity: 1, // default 1 (IMPORTANT)
                    stock: product?.stock || 0,
                    unit: product?.unit || "",
                    gstRate: product?.gstRate || 0,
                    taxType: product?.taxType || "exclusive",
                  };

                  return { ...prev, items: updated };
                });
              }}
              placeholder="Search product..."
              minInputLength={2}
              debounceWait={400}
              defaultOptions={cachedProductOptions}
              isClearable
            />
          </div>
        ),
      },
      {
        name: "Quantity",
        cell: (row, index) => (
          <input
            type="text"
            className={`form-control form-control-sm ${
              errors[`qty_${index}`] ? "border-danger" : ""
            }`}
            value={row.quantity}
            onChange={(e) =>
              handleItemChange(index, "quantity", e.target.value)
            }
          />
        ),
      },
      {
        name: "Selling Price (₹)",
        cell: (row, index) => (
          <div>
            <input
              type="text"
              className="form-control"
              value={row.salePrice}
              onChange={(e) =>
                handleItemChange(index, "salePrice", e.target.value)
              }
            />
          </div>
        ),
      },
      {
        name: "Total (₹)",
        cell: (row, index) => (
          <input
            className="form-control"
            value={(row.quantity * row.salePrice).toFixed(2)}
            readOnly
          />
        ),
      },
      {
        name: "Action",
        center: true,
        cell: (_, index) => (
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={() => removeItem(index)}
          >
            <i className="bi bi-trash"></i>
          </button>
        ),
      },
    ],
    [orderData.items],
  );
  const validateForm = () => {
    const newErrors = {};

    // Vendor required
    if (!orderData.vendor) {
      newErrors.vendor = "Customer is required";
    }

    // Validate items
    orderData.items.forEach((item, index) => {
      if (!item.productId) {
        newErrors[`product_${index}`] = `Select product in row ${index + 1}`;
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`qty_${index}`] = `Enter valid quantity in row ${index + 1}`;
      }
    });

    setErrors(newErrors);

    //Focus first product error
    const firstProductIndex = orderData.items.findIndex(
      (item) => !item.productId,
    );

    // Show first error toast
    const firstError = Object.values(newErrors)[0];
    if (firstError) toast.error(firstError);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!orderData.vendor) {
      toast.error("Please select a customer");
      return;
    }

    setLoading(true);
    try {
      const items = orderData.items.map((item, i) => {
        if (!item.productId) throw new Error(`Select product for row ${i + 1}`);
        if (!item.quantity || item.quantity < 1) throw new Error("Qty ≥ 1");

        return {
          productId: item.productId,
          sellingQty: Number(item.quantity),
          sellingPrice: Number(item.salePrice),
        };
      });

      const payload = {
        customerId: orderData.vendor,
        items,
        receivedAmount: Number(orderData.receivedAmount) || 0,
        paymentMode: orderData.paymentMode,
        notes: orderData.notes,
        saleDate: orderData.saleDate,
      };

      let res;

      if (isEdit) {
        res = await updateSaleOrder(id, payload);
        toast.success("Sale Order Updated!");
      } else {
        res = await createSaleOrder(payload);
        toast.success("Sale Order Created Successfully");
      }

      navigate("/sales/order/list");
      clearSearchCache();
    } catch (err) {
      toast.error(err.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="main">
      <div className="d-flex align-items-center mb-4 gap-2">
        <Link to="/sales/order/list">
          <ArrowLeft size={20} onClick={() => clearSearchCache()} />
        </Link>
        <h2 className="mb-0 custom-title">
          {isEdit ? "Edit Sale Order" : "Create Sale Order"}
        </h2>
      </div>

      <form onSubmit={handleSubmit}>
        {/* customer + date same */}
        <div className="row g-3 mb-4">
          <div className="col-md-6 mb-3">
            <label className="form-label">
              Customer <span className="text-danger">*</span>
            </label>

            <AsyncSearchSelect
              value={
                orderData.vendor
                  ? {
                      label: orderData.vendorName || "Selected Customer",
                      value: orderData.vendor,
                    }
                  : null
              }
              loadOptions={loadPartyOptions}
              onChange={(selected) => {
                setOrderData((prev) => ({
                  ...prev,
                  vendor: selected?.value || "",
                  vendorName: selected?.label || "",
                }));

                setErrors((prev) => ({ ...prev, vendor: null }));
              }}
              placeholder="Search customer..."
              minInputLength={2}
              debounceWait={500}
              isClearable
              defaultOptions={cachedOptions}
            />

            {errors.vendor && (
              <small className="text-danger">{errors.vendor}</small>
            )}
          </div>

          <div className="col-md-3">
            <label className="form-label">Sale Date</label>
            <input
              type="date"
              className="form-control"
              value={orderData.saleDate}
              min={new Date().toLocaleDateString("en-CA")}
              onChange={(e) =>
                setOrderData((p) => ({ ...p, saleDate: e.target.value }))
              }
            />
          </div>
        </div>
        <div className="card">
          <div className="card-body p-0 rounded">
            {/*  DataTable */}
            <DataTable
              columns={columns}
              customStyles={customStyles}
              data={orderData.items}
              dense
              noHeader
            />
          </div>
        </div>

        <div className="text-end mt-3 fw-semibold">
          Grand Total: ₹ {grandTotal.toFixed(2)}
        </div>

        <button
          type="button"
          className="btn btn-sm btn-primary mt-2"
          onClick={addItem}
        >
          + Add Item
        </button>

        <div className="d-flex justify-content-end gap-3 mt-4">
          <button
            type="button"
            className="btn btn-custom"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancel
          </button>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update Order" : "Save Order"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default headerLayoutWrapper(SaleOrderPage);
